import './App.css'

import { scan } from 'react-scan'
import { ApolloProvider } from '@apollo/client'
import { RouterProvider } from 'react-router-dom'

import rootRouter from '@/router/root'
import { useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { captureAttribution } from '@/services/Attribution/attributionStorage'
import initApollo from './apollo'
import { GlobalContextProvider } from './context/GlobalContext'
import { LoadingProvider } from './context/LoadingContext'
import { initializeDatadogRUM } from './utils/datadog'
import { OptimizelyProvider } from './optimizely/OptimizelyContext'
import { analyticsService } from './services/Analytics'
import { bootParamsService } from './services/BootParams'
import { ChatClientProvider } from './context/ChatClientContext'
import { ChatV2Provider } from './chatV2/providers/ChatV2Provider'
import { SnackbarProvider } from './context/SnackbarContext'
import { UserGuidingProvider } from './components/UserGuiding'
import { userGuidingService } from './services/UserGuiding'
import { NotificationProvider } from './context/NotificationContext'
import { useFeatureFlags } from './optimizely/useFeatureFlags'
import { FeatureFlag } from './optimizely/types'
import RumUserIdentity from './components/RumUserIdentity/RumUserIdentity'

const ENV = import.meta.env.VITE_VERCEL_ENV

if (ENV === 'development' || ENV === 'preview') {
  scan({
    enabled: true,
    animationSpeed: 'slow',
  })
}

initializeDatadogRUM()
analyticsService.initialize()
bootParamsService.initialize()
// ENG-1955: capture any ad click ID the landing page forwarded, before
// routing can strip the query string.
captureAttribution()
userGuidingService.loadScript()

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const featureFlags = useFeatureFlags()
  const isChatV2 = featureFlags[FeatureFlag.MARKETPLACE_CHAT_V2]

  if (isChatV2) {
    return <ChatV2Provider>{children}</ChatV2Provider>
  }

  return <ChatClientProvider>{children}</ChatClientProvider>
}

const App = () => {
  const client = initApollo()
  const queryClient = new QueryClient()
  // TODO: replace with refs for performance?
  const [preventNavigation, setPreventNavigation] = useState(false)
  const [selectedTab, setSelectedTab] = useState('')
  const [globalPermissions, setGlobalPermissions] = useState()

  return (
    <ApolloProvider client={client}>
      {/* Above OptimizelyProvider on purpose: that provider returns null
          until flags load, and RUM identity must not depend on it. */}
      <RumUserIdentity />
      <QueryClientProvider client={queryClient}>
        <OptimizelyProvider>
          <UserGuidingProvider>
            <GlobalContextProvider
              value={{
                preventNavigation,
                setPreventNavigation,
                selectedTab,
                setSelectedTab,
                globalPermissions,
                setGlobalPermissions,
              }}
            >
              <NotificationProvider>
                <ChatProvider>
                  <SnackbarProvider>
                    <LoadingProvider>
                      <RouterProvider router={rootRouter} />
                    </LoadingProvider>
                  </SnackbarProvider>
                </ChatProvider>
              </NotificationProvider>
            </GlobalContextProvider>
          </UserGuidingProvider>
        </OptimizelyProvider>
      </QueryClientProvider>
    </ApolloProvider>
  )
}

export default App
